import { ApplicationController, randomId } from '@aha-app/mvc';
import { type IPoint } from 'fabric/fabric-impl';
import moment from 'moment';
import Serialization from '../collaboration/Serialization';
import Canvas from '../objects/Canvas';
import {
  hasIntegrationRecordReference,
  isSimpleGroup,
} from '../objects/guards';
import type {
  CreateRecord,
  IntegrationRecordData,
  JiraRecordTypeKey,
  JiraWhiteboardIntegration,
  NewRecords,
  RecordFieldData,
  RecordId,
  Result,
  WhiteboardIntegration,
  WriteCapableWhiteboardIntegration,
} from '../shared-types';
import { makeCreateIntegrationRecordTool } from '../tools/ToolCreateRecords';
import { type IntegrationShape } from '../types';
import trackEvent from '../utils/analytics';
import { escapeTextContent } from '../utils/html';
import type WhiteboardController from './WhiteboardController';

const DATA_REFRESH_THRESHOLD_SECONDS = 30;
const PROMPT_DURATION_SECONDS = 30;

const integrationsEnabled = () => window.currentAccount?.whiteboardsAdvanced;

export interface IntegrationRecordReference {
  type: 'recordReference';
  recordType: string;
  recordKey: string;
  recordUrl: string;
}

interface PendingRecordData {
  records: IntegrationRecordReference[];
  position: IPoint;
}

type PastedRecord = Partial<IntegrationShape>;

export interface IntegrationsControllerProps {
  integrations?: Record<string, WhiteboardIntegration>;
  readOnly: boolean;
}

const isWriteCapableWhiteboardIntegration = (
  integration: WhiteboardIntegration
): integration is WriteCapableWhiteboardIntegration<any> =>
  'createRecords' in integration;

interface ActiveAuthPromptStatus {
  id: number;
  status: 'active';
  promptType: 'importModal' | 'message' | 'exportModal';
  service: WhiteboardIntegration;
  mode: 'connect' | 'reconnect';
  action: (confirm: boolean) => void;
}
interface DismissedAuthPromptStatus {
  status: 'dismissed';
}
export type AuthPromptStatus =
  | ActiveAuthPromptStatus
  | DismissedAuthPromptStatus;

interface RecordCreationData {
  service: string;
  data: CreateRecord[];
}
interface IntegrationsControllerState {
  confirmations: Record<string, boolean>;
  authPromptStatus?: AuthPromptStatus;
  recordCreationData?: RecordCreationData;
}

interface RecordCreationProps {
  integration: JiraWhiteboardIntegration;
  integrationsController: IntegrationsController;
  data: CreateRecord[];
  cancel: () => void;
}

const withTimeout = async <T>(promise: Promise<T>, timeoutSeconds: number) =>
  await Promise.race([
    promise,
    new Promise<void>(resolve =>
      setTimeout(() => resolve(), timeoutSeconds * 1000)
    ),
  ]);

class IntegrationsController extends ApplicationController<
  IntegrationsControllerState,
  IntegrationsControllerProps
> {
  static initialState: IntegrationsControllerState = {
    confirmations: {},
  };

  whiteboardController: WhiteboardController;
  cleanup?: () => void;

  startController(canvas: Canvas, whiteboardController: WhiteboardController) {
    this.whiteboardController = whiteboardController;

    // Confirmations is an object like { jira: false, ado: true }
    this.state.confirmations = Object.keys(this.integrations).reduce(
      (acc: Record<string, boolean>, service) => {
        acc[service] = this.getIntegration(service)?.hasAuth() ?? false;
        return acc;
      },
      {}
    );

    const callback = ({ target }: { target?: fabric.Object }) => {
      if (target) {
        this.recordAdded(target);
      }
    };
    canvas.on('object:added', callback);

    this.cleanup = () => {
      canvas.off('object:added', callback);
    };
  }

  destroy() {
    this.cleanup?.();
  }

  actionAddRecord(record: IntegrationRecordReference, position: IPoint) {
    const service = record.recordType;

    const integration = this.getIntegration(service);
    if (!integration) {
      return;
    }

    const recordData: PendingRecordData = {
      records: [record],
      position,
    };

    void this.loadAndInsert(recordData);
  }

  async confirmModal(
    promptType: 'importModal' | 'message' | 'exportModal',
    service: string,
    hasAuth: boolean
  ) {
    // allow the modal to replace the prompt, but not the other way around
    if (
      promptType === 'message' &&
      this.state.authPromptStatus?.status === 'active' &&
      ['importModal', 'exportModal'].includes(
        this.state.authPromptStatus.promptType
      )
    ) {
      return false;
    }

    const promptId = Math.random();

    const promise = new Promise<boolean>(resolve => {
      this.state.authPromptStatus = {
        id: promptId,
        status: 'active',
        promptType,
        service: this.getIntegration(service),
        mode: hasAuth ? 'reconnect' : 'connect',
        action: (confirm: boolean) => {
          resolve(confirm);
        },
      };
    });

    // time out the message, but not the prompt
    const value = await (promptType === 'message'
      ? withTimeout(promise, PROMPT_DURATION_SECONDS)
      : promise);

    // bail if another prompt has taken its place
    if (
      this.state.authPromptStatus?.status === 'active' &&
      this.state.authPromptStatus.id !== promptId
    ) {
      return false;
    }

    this.state.authPromptStatus =
      value === false ? { status: 'dismissed' } : undefined;

    return value;
  }

  get integrations() {
    return this.props.integrations ?? {};
  }

  getIntegration(service: 'jira'): JiraWhiteboardIntegration | undefined;
  getIntegration(service: string): WhiteboardIntegration;
  getIntegration(service: string) {
    // don't do anything integration-related if the user isn't allowed to update the whiteboard
    if (!this.whiteboardController.props.allowUpdate) {
      return;
    }

    if (!this.props.integrations) {
      console.warn('No integrations configured');
      return;
    }

    return this.props.integrations[service];
  }

  getCreateIntegrationRecordTools() {
    return Object.values(this.integrations)
      .filter(isWriteCapableWhiteboardIntegration)
      .map(makeCreateIntegrationRecordTool);
  }

  async actionStartRecordCreation(service: 'jira', data: CreateRecord[]) {
    trackEvent(
      'Feature-7351856624664895072',
      'User opens convert whiteboard shape to Jira'
    );

    const integration = this.getIntegration(service);

    if (!integration) {
      console.warn(`Integration not found for ${service}`);
      return;
    }

    const hasAuth = integration.hasAuth();

    // there's no way to trigger auth without making a request, so if the user has an auth token, attempt
    // an API request up front in order to see if it's valid. The cached result will
    // be reused by the modal once it gets to that stage. If no existing auth, skip this step
    let projects: Result<unknown> | undefined;
    if (hasAuth) {
      projects = await integration.getSites({
        options: { authenticate: false },
      });
    }

    if (projects?.status !== 'success') {
      // if the previous request failed, prompt the user to re-authenticate then try again
      const confirmed = await this.confirmModal(
        'exportModal',
        service,
        hasAuth
      );

      if (!confirmed) {
        // user dismissed prompt, so do nothing
        return;
      }

      // this will trigger the auth screen now the user has confirmed they want to authenticate
      projects = await integration.getSites({
        options: { authenticate: true },
      });
    }

    if (projects?.status !== 'success') {
      this.whiteboardController.actionShowMessage({
        message: projects.error,
        type: 'error',
      });
      return;
    }

    this.state.recordCreationData = {
      service,
      data,
    };
  }

  actionCancelRecordCreation() {
    this.state.recordCreationData = undefined;
  }

  async actionCreateRecords(
    service: 'jira',
    key: JiraRecordTypeKey,
    records: NewRecords,
    fieldData: RecordFieldData
  ) {
    const integration = this.getIntegration(service);

    if (!integration) {
      return;
    }

    const data = await integration.createRecords({
      key,
      records,
      fieldData,
      options: {
        authenticate: true,
      },
    });

    if (data.status !== 'success') {
      this.whiteboardController.actionShowMessage({
        message: data.error,
        type: 'error',
      });
      return;
    }

    // Once the records are created, find their corresponding shapes in the whiteboard,
    // and link them to the new record
    const linkedShapes: IntegrationShape[] = [];
    for (const [shapeId, recordData] of Object.entries(data.data.records)) {
      const shape = this.whiteboardController.dataStoreObjects.findRecordById(
        shapeId as RecordId
      ) as IntegrationShape;

      const integrationRecord = {
        recordType: service,
        recordKey: recordData.key,
        recordUrl: recordData.url,
        createdAt: new Date().toISOString(),
      };
      shape.set({ integrationRecord });

      linkedShapes.push(shape);
    }

    // kick off an update which will fill in the record data for the linked records
    void this.loadAndUpdate(undefined, linkedShapes);

    this.state.recordCreationData = undefined;
    this.whiteboardController.canvas.requestRenderAll();

    const count = Object.keys(records).length;
    this.whiteboardController.actionShowMessage({
      message: `${count} ${integration.getRecordTitle(
        count !== 1,
        false
      )} created in ${integration.title}`,
      type: 'success',
    });
  }

  getRecordCreationProps(): RecordCreationProps | undefined {
    if (!this.state.recordCreationData) {
      return;
    }

    const integration = this.getIntegration(
      this.state.recordCreationData.service
    ) as JiraWhiteboardIntegration;

    if (!integration) {
      return;
    }

    return {
      integration,
      integrationsController: this,
      data: this.state.recordCreationData.data,
      cancel: () => this.actionCancelRecordCreation(),
    };
  }

  private async loadAndInsert(recordData: PendingRecordData) {
    const controller = this.whiteboardController;
    const { canvas } = controller;

    const objects = recordData.records.reduce<Record<RecordId, PastedRecord>>(
      (acc, record) => {
        const id = randomId() as RecordId;
        acc[id] = this.makeShape(id, record, recordData.position);
        return acc;
      },
      {}
    );

    const newDoc = { objects };

    // add the document to the canvas
    Serialization.restoreDocument(controller, newDoc);

    const restoredObjects = Serialization.getUngroupedObjects(canvas, newDoc);

    restoredObjects.forEach(object => {
      object.fire('created');
    });

    const selectedObject = canvas.makeActiveSelection(restoredObjects);
    selectedObject.bringToFront();

    // update all stale records, passing the new
    await this.loadAndUpdate(
      restoredObjects.filter(hasIntegrationRecordReference)
    );
  }

  private async loadRecords(
    records: IntegrationRecordReference[],
    options: { promptType: 'importModal' | 'message' }
  ) {
    const integrations = records.reduce((acc, record) => {
      if (!acc.has(record.recordType)) {
        acc.set(record.recordType, {
          integration: this.getIntegration(record.recordType),
          records: [],
        });
      }
      acc.get(record.recordType)!.records.push(record);
      return acc;
    }, new Map<string, { integration: WhiteboardIntegration | undefined; records: IntegrationRecordReference[] }>());

    if (integrations.size === 0) {
      return;
    }

    const errors: string[] = [];
    const foundRecords: IntegrationRecordData[] = [];

    for (const [service, { integration, records }] of integrations.entries()) {
      if (!integration) {
        console.warn(`Integration not found for ${service}`);
        return;
      }

      const hasAuth = integration.hasAuth();

      let data: Result<IntegrationRecordData[]> | undefined;

      const recordUrls = records.map(r => r.recordUrl).filter(Boolean);

      // if no existing auth, skip the first load as we know it'll fail
      if (hasAuth) {
        data = await integration.getRecords({
          recordUrls,
          options: { authenticate: false },
        });
      }

      if (data?.status !== 'success') {
        // don't show the message prompt again, if the user dismissed it
        if (
          this.state.authPromptStatus?.status === 'dismissed' &&
          options.promptType === 'message'
        ) {
          return;
        }

        const confirmed = await this.confirmModal(
          options.promptType,
          service,
          hasAuth
        );

        if (!confirmed) {
          // user dismissed prompt, so do nothing
          return;
        }

        data = await integration.getRecords({
          recordUrls,
          options: { authenticate: true },
        });
      }

      if (data?.status === 'success') {
        foundRecords.push(...data.data);
      } else {
        errors.push(data.error);
      }
    }

    if (errors.length > 0) {
      this.whiteboardController.actionShowMessage({
        message: errors.join(', '),
        type: 'error',
      });
      return;
    }

    return foundRecords;
  }

  private makeShape(
    id: RecordId,
    integrationRecord: IntegrationRecordReference,
    position: IPoint
  ): PastedRecord {
    return {
      id,
      authorId: this.whiteboardController.state.userId,
      type: 'shape',
      shapeName: 'integrationRecord',
      integrationRecord: {
        ...integrationRecord,
        createdAt: new Date().toISOString(),
      },
      left: position.x,
      top: position.y,
    };
  }

  private async loadAndUpdate(
    newShapes?: IntegrationShape[],
    linkedShapes?: IntegrationShape[]
  ) {
    if (!integrationsEnabled()) return;

    const { undoManager, canvas } = this.whiteboardController;
    if (!canvas) {
      return;
    }

    const collectShapes = (obj: Canvas | fabric.Object): IntegrationShape[] => {
      if (obj instanceof Canvas || isSimpleGroup(obj)) {
        return obj.getObjects().flatMap(collectShapes);
      } else if (hasIntegrationRecordReference(obj) && this.shouldUpdate(obj)) {
        return [obj];
      } else {
        return [];
      }
    };

    const staleShapes = collectShapes(canvas);
    const undoEntry = undoManager.currentUndoEntry();

    const records = await this.loadRecords(
      staleShapes
        .concat(newShapes ?? [])
        .concat(linkedShapes ?? [])
        .map(shape => ({
          type: 'recordReference',
          recordType: shape.integrationRecord.recordType,
          recordKey: shape.integrationRecord.recordKey,
          recordUrl:
            // fall back to recordData value for backwards compatibility, since the shape may have been
            // added before the recordUrl field was added
            shape.integrationRecord.recordUrl ??
            shape.integrationRecord.recordData?.url,
        })),
      { promptType: newShapes ? 'importModal' : 'message' }
    );

    if (records) {
      const doUpdate = (shape: IntegrationShape) => {
        const recordData = records.find(
          r => r.recordId === shape.integrationRecord.recordKey
        );
        if (recordData) {
          shape.set(['integrationRecord', 'recordData'], recordData);
          shape.set(
            ['integrationRecord', 'updatedAt'],
            new Date().toISOString()
          );
          shape.set(
            'textContent',
            `<p>${escapeTextContent(recordData.name)}</p>`
          );
        }
      };
      // updates to any existing shapes should not be undoable
      undoManager.withoutUndo(() => {
        for (const shape of staleShapes) {
          doUpdate(shape);
        }
        for (const shape of linkedShapes ?? []) {
          doUpdate(shape);
        }
      });
      // updates to new shapes are added to the existing undo entry, so we don't undo back to the loading state
      if (newShapes) {
        undoManager.addUndo(undoEntry, () => {
          for (const shape of newShapes) {
            doUpdate(shape);
          }
        });
        undoManager.completeUndoGroup();
      }
    } else if (newShapes) {
      undoManager.withoutUndo(() => {
        // remove any new shapes since we failed to load them
        canvas.remove(...newShapes);
        for (const obj of newShapes) {
          obj.deleted(this.whiteboardController);
        }
      });
      undoManager.removeUndo(undoEntry);
    }
    canvas.requestRenderAll();
  }

  private shouldUpdate(shape: IntegrationShape) {
    const integrationRecord = shape.integrationRecord;

    // don't update anything that's only just been added, unless it hasn't been filled in yet
    const createdAt = integrationRecord.createdAt;
    if (
      integrationRecord.recordData &&
      createdAt &&
      moment().diff(createdAt, 'second') < DATA_REFRESH_THRESHOLD_SECONDS
    ) {
      return false;
    }

    const updatedAt = integrationRecord.updatedAt;

    if (!updatedAt) {
      return true;
    }

    return moment().diff(updatedAt, 'second') >= DATA_REFRESH_THRESHOLD_SECONDS;
  }

  private recordUpdateTimer?: ReturnType<typeof setTimeout>;

  private recordAdded(object: fabric.Object) {
    if (hasIntegrationRecordReference(object) && this.shouldUpdate(object)) {
      clearTimeout(this.recordUpdateTimer);

      this.recordUpdateTimer = setTimeout(() => {
        void this.loadAndUpdate();
      }, 500);
    }
  }
}

export default IntegrationsController;
